import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Alert } from '@syneto/compass-react';
import { GET_EDITIONS_DEFAULT_QUERY_PARAMS } from '../../../../constants/editions';
import { getProductAddonDetails, getProductAddons, removeProductAddonDetails } from '../../../../data/productAddons/actions';
import {
	getEditions,
	hideEditionEditModal,
	hideEditionViewModal,
	hideLicenseEditModal,
	showEditionEditModal,
	showEditionViewModal,
	showLicenseEditModal,
	updateEditionLicenses
} from '../../../../data/editions/actions';
import LicenseEditModal from '../ProductAddons/Modals/EditModal';
import { licenseById } from './editionDisplay';
import EditionsTable from './editionsTable';
import EditionViewModal from './editionViewModal';
import EditionEditModal from './editionEditModal';

/*
 * Mounted in the Operations and the Administrator module alike: both roles may edit an edition,
 * and every screen is gated to one role per module, so the page lives here and the Administrator
 * routes import it, as they do AdminMaintenanceTypes the other way round.
 */
const Editions = (props) => {
	const {
		editions,
		pagination,
		editionsLoading,
		editionsError,
		saving,
		viewedEdition,
		editedEdition,
		editedLicenseId,
		catalog,
		catalogLoading,
		catalogFailed,
		getEditions,
		getProductAddons,
		getProductAddonDetails,
		removeProductAddonDetails,
		updateEditionLicenses,
		showViewModalFor,
		hideViewModal,
		showEditModalFor,
		hideEditModal,
		showLicenseEditModalFor,
		hideLicenseEditModal
	} = props;

	const [queryParams, setQueryParams] = useState(GET_EDITIONS_DEFAULT_QUERY_PARAMS);

	/*
	 * A write outlives its request: it goes on to poll a job for up to a minute. Navigating away
	 * mid-poll has to stop it, otherwise it dispatches into an unmounted page and pushes a
	 * notification about work nobody is watching any more.
	 */
	const abortRef = useRef(null);

	useEffect(() => {
		abortRef.current = new AbortController();

		return () => {
			abortRef.current?.abort();
		};
	}, []);

	/*
	 * The license catalog names the licenses and tiers an edition holds as ids. Fetched
	 * unconditionally rather than reused-if-present: a tier picked here has to be a current one.
	 */
	useEffect(() => {
		getProductAddons();
	}, []);

	useEffect(() => {
		getEditions(queryParams);
	}, [queryParams]);

	const onPaginationChange = (page, pageSize) => {
		setQueryParams((current) => {
			return { ...current, page: page, pageSize: pageSize };
		});
	};

	/*
	 * The license form is the licenses page's own, opened here on top of the edition dialog. It
	 * takes its values from the license details in the store, so those are fetched first, as the
	 * licenses page does, and cleared when the form closes. A saved license refreshes the catalog,
	 * which is where the edition dialogs take their tier names from.
	 */
	const editedLicense = licenseById(catalog, editedLicenseId);

	useEffect(() => {
		if (editedLicenseId) {
			getProductAddonDetails(editedLicenseId);
		}
	}, [editedLicenseId]);

	const onCloseLicenseEditModal = () => {
		removeProductAddonDetails();
		hideLicenseEditModal();
	};

	const onSave = (id, licenses) => {
		return updateEditionLicenses(id, licenses, { queryParams: queryParams, signal: abortRef.current?.signal });
	};

	return (
		<div id="editions" className="p-4 bg-white w-100">
			<div className="col-12">
				{editionsError && <Alert variant="danger">{editionsError}</Alert>}
				{catalogFailed && <Alert variant="danger">The license catalog could not be loaded, so license and tier names may be missing.</Alert>}

				<EditionsTable
					editions={editions}
					pagination={pagination}
					catalog={catalog}
					isLoading={editionsLoading || catalogLoading}
					hasError={Boolean(editionsError)}
					isSaving={saving}
					onPaginationChange={onPaginationChange}
					onView={showViewModalFor}
					onEdit={showEditModalFor}
				/>

				<EditionViewModal
					edition={viewedEdition}
					catalog={catalog}
					isSaving={saving}
					suspended={Boolean(editedLicense)}
					onEdit={showEditModalFor}
					onEditLicense={showLicenseEditModalFor}
					onClose={hideViewModal}
				/>

				<EditionEditModal
					edition={editedEdition}
					catalog={catalog}
					isSaving={saving}
					suspended={Boolean(editedLicense)}
					onSave={onSave}
					onEditLicense={showLicenseEditModalFor}
					onCancel={hideEditModal}
				/>

				{editedLicense && (
					<LicenseEditModal
						editedAddon={editedLicense}
						afterUpdateCallback={getProductAddons}
						onHide={onCloseLicenseEditModal}
					/>
				)}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		editions: state.editions.editions,
		pagination: state.editions.pagination,
		editionsLoading: state.editions.editionsLoading,
		editionsError: state.editions.editionsError,
		saving: state.editions.saving,
		viewedEdition: state.editions.viewedEdition,
		editedEdition: state.editions.editedEdition,
		editedLicenseId: state.editions.editedLicenseId,
		/*
		 * Read from the license catalog slice rather than fetched into this one: it is the same
		 * unpaginated endpoint the Product Licenses page shows, and a second copy could disagree
		 * with it about which tiers exist.
		 */
		catalog: state.productAddons.data,
		catalogLoading: state.productAddons.loading,
		catalogFailed: state.productAddons.status === 'failed'
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getEditions: (queryParams) => { return dispatch(getEditions(queryParams)); },
		getProductAddons: () => { return dispatch(getProductAddons()); },
		getProductAddonDetails: (id) => { return dispatch(getProductAddonDetails(id)); },
		removeProductAddonDetails: () => { return dispatch(removeProductAddonDetails()); },
		updateEditionLicenses: (id, licenses, options) => { return dispatch(updateEditionLicenses(id, licenses, options)); },
		showViewModalFor: (edition) => { return dispatch(showEditionViewModal(edition)); },
		hideViewModal: () => { return dispatch(hideEditionViewModal()); },
		showEditModalFor: (edition) => { return dispatch(showEditionEditModal(edition)); },
		hideEditModal: () => { return dispatch(hideEditionEditModal()); },
		showLicenseEditModalFor: (licenseId) => { return dispatch(showLicenseEditModal(licenseId)); },
		hideLicenseEditModal: () => { return dispatch(hideLicenseEditModal()); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Editions);
